import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

const SalesSummaryReport = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);

  /* ================= FILTER STATE ================= */
  const [today, setToday] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  /* ================= LOAD SALES SUMMARY ================= */
  const loadSummary = async ({ today, dateFrom, dateTo }) => {
    dispatch(loadingTrue());

    const params = new URLSearchParams();
    params.append("org_id", user.org_id);

    if (today) params.append("period", "today");
    if (dateFrom) params.append("date_from", dateFrom.toISOString().slice(0, 10));
    if (dateTo) params.append("date_to", dateTo.toISOString().slice(0, 10));

    const res = await Handler({
      method: "get",
      url: `/reports/sales_summary.php?${params.toString()}`
    });

    dispatch(loadingFalse());

    if (!res?.success) {
      toast.error(res?.msg || "Failed");
      return;
    }

    /* 🔥 SAFETY: data / message */
    const payload = res.data || res.message;

    setRows(payload?.rows || []);
    setTotals(payload?.totals || null);
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (user?.org_id) loadSummary({ today, dateFrom, dateTo });
  }, [user?.org_id]);

  /* ================= AUTO FILTER ================= */
  useEffect(() => {
    if (!user?.org_id) return;
    loadSummary({ today, dateFrom, dateTo });
  }, [today, dateFrom, dateTo, user?.org_id]);


  /* ================= HANDLERS ================= */
  const handleToday = () => {
    setToday(!today);
    setDateFrom(null);
    setDateTo(null);
  };

  const handleDateFrom = (d) => {
    setToday(false);
    setDateFrom(d);
  };

  const handleDateTo = (d) => {
    setToday(false);
    setDateTo(d);
  };

  /* ================= CHART DATA ================= */



  const chartData = rows.map(r => ({
    date: r.sdate,
    Bills: r.bills,
    Amount: Number(r.sales_amount),
    Collection: Number(r.collections)
  }));


  return (
    <>

      {/* ================= FILTER BAR ================= */}
      <div className="filter-bar mb-4">
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <Button
              color={today ? "primary" : "outline-primary"}
              onClick={handleToday}
            >
              Today
            </Button>
          </Col>

          <Col md={3}>
            <label className="small">Date From</label>
            <DatePicker
              selected={dateFrom}
              onChange={handleDateFrom}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
            />
          </Col>

          <Col md={3}>
            <label className="small">Date To</label>
            <DatePicker
              selected={dateTo}
              onChange={handleDateTo}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
              minDate={dateFrom}
            />
          </Col>
        </Row>
      </div>

      {/* ================= KPI CARDS ================= */}
      {totals && (
        <Row className="mb-4 g-3">
          {/* <Col md={3}><div className="kpii-card kpii-blue"><div className="kpii-label">Bills</div><div className="kpii-value">{totals.bills}</div></div></Col> */}
          <Col md={6}><div className="kpii-card kpii-purple"><div className="kpii-label">Items Sold</div><div className="kpii-value">{totals.items_qty}</div></div></Col>
          <Col md={6}><div className="kpii-card kpii-green"><div className="kpii-label">Net Sales</div><div className="kpii-value">₹ {Number(totals.net_sales).toFixed(2)}</div></div></Col>
          {/* <Col md={4}><div className="kpii-card kpii-red"><div className="kpii-label">Outstanding</div><div className="kpii-value">₹ {Number(totals.outstanding).toFixed(2)}</div></div></Col> */}
        </Row>
      )}

      {/* ================= BAR CHART ================= */}
      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="chart-card" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={12}>
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="Bills" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Amount" fill="#22c55e" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Collection" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <div className="billing-table-card">
        <div className="table-responsive">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="right">Bills</th>
                <th className="right">Items Qty</th>
                <th className="right">Sales</th>
                <th className="right">Discount</th>
                <th className="right">Loyalty Redeemed</th>
                <th className="right">Collections</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.sdate}</td>
                  <td className="right">{r.bills}</td>
                  <td className="right">{r.items_qty}</td>
                  <td className="right">₹ {Number(r.sales_amount).toFixed(2)}</td>
                  <td className="right">₹ {Number(r.discount).toFixed(2)}</td>
                  <td className="right">₹ {Number(r.loyalty_redeemed).toFixed(2)}</td>
                  <td className={`right ${Number(r.collections) > 0 ? "success" : "muted"}`}>
                    ₹ {Number(r.collections).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </>
  );
};

export default SalesSummaryReport;
